/**
 * Analytics consent for the dashboard (ADR-051 §5). The browser OTel SDK
 * (`lib/otel.ts`) only starts once the user has accepted; until then — and
 * after a refusal — nothing OTel-related is loaded, read, stored, or sent.
 *
 * The record lives in `localStorage` under `kopai-dashboard-consent` as
 * `{ analytics, version, timestamp }`. A record written under a different
 * `CONSENT_VERSION`, or one that does not parse, counts as "no choice yet":
 * the banner shows again and the SDK stays off. Bump `CONSENT_VERSION`
 * whenever what the analytics collect changes materially.
 *
 * Same-tab changes notify subscribers directly; other tabs learn through the
 * browser's `storage` event, so a choice made in one tab is applied in all.
 */

export const CONSENT_STORAGE_KEY = "kopai-dashboard-consent";
export const CONSENT_VERSION = "2026-10-08";

/**
 * Tab-scoped OTel session id (ADR-051 §1), owned by `lib/otel-session.ts`.
 * Declared here, in a module with no dependencies, so the consent gate can
 * delete it without pulling the OpenTelemetry bundle into the main chunk.
 */
export const OTEL_SESSION_STORAGE_KEY = "kopai-otel-session";

/** Policies live on the marketing site (kopai.app), not in the dashboard. */
export const PRIVACY_POLICY_URL = "https://kopai.app/privacy/";
export const COOKIE_POLICY_URL = "https://kopai.app/cookies/";

export interface ConsentRecord {
  analytics: boolean;
  version: string;
  timestamp: number;
}

/** `unset` = no valid choice recorded for the current `CONSENT_VERSION`. */
export type AnalyticsConsent = "granted" | "denied" | "unset";

/**
 * Parses a stored value. Anything other than a well-formed record for the
 * current `CONSENT_VERSION` returns `null` ("no choice yet").
 */
export function parseConsent(raw: string | null): ConsentRecord | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { analytics, version, timestamp } = parsed as Record<string, unknown>;
    if (typeof analytics !== "boolean") return null;
    if (version !== CONSENT_VERSION) return null;
    if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) {
      return null;
    }
    return { analytics, version, timestamp };
  } catch {
    return null;
  }
}

// When `localStorage` is unavailable or refuses the write (blocked storage,
// some private-browsing modes), the choice still holds for this page so the
// banner doesn't reappear straight after a click. It is not persisted, so
// the next page load asks again.
let volatileRecord: ConsentRecord | null = null;
let useVolatile = false;

export function readConsent(): ConsentRecord | null {
  if (useVolatile) return volatileRecord;
  try {
    return parseConsent(localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return volatileRecord;
  }
}

export function getAnalyticsConsent(): AnalyticsConsent {
  const record = readConsent();
  if (!record) return "unset";
  return record.analytics ? "granted" : "denied";
}

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** Records the user's choice and notifies same-tab subscribers. */
export function setAnalyticsConsent(analytics: boolean): ConsentRecord {
  const record: ConsentRecord = {
    analytics,
    version: CONSENT_VERSION,
    timestamp: Date.now(),
  };
  volatileRecord = record;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    useVolatile = false;
  } catch {
    useVolatile = true;
  }
  notify();
  return record;
}

function onStorage(event: StorageEvent): void {
  // `key === null` means another tab called `localStorage.clear()`.
  if (event.key !== null && event.key !== CONSENT_STORAGE_KEY) return;
  notify();
}

/**
 * Subscribes to consent changes from this tab (`setAnalyticsConsent`) and
 * other tabs (`storage` event). Shaped for `useSyncExternalStore`.
 */
export function subscribeToConsent(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** Reset module state for tests. */
export function resetConsentStateForTests(): void {
  volatileRecord = null;
  useVolatile = false;
}
